package br.com.vvrefeicoes.portal.feature.juridico

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.domain.model.JuridicoEmpresa
import br.com.vvrefeicoes.portal.domain.repository.JuridicoEmpresaRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class JuridicoEmpresasController(
    private val repository: JuridicoEmpresaRepository,
    private val scope: CoroutineScope,
) {
    var empresas by mutableStateOf<List<JuridicoEmpresa>>(emptyList())
        private set
    var loading by mutableStateOf(false)
        private set
    var busy by mutableStateOf(false)
        private set
    var errorMessage by mutableStateOf<String?>(null)
        private set
    var totalPages by mutableStateOf(1)
        private set
    var page by mutableStateOf(0)
        private set

    var nomeFiltro by mutableStateOf("")
    var cnpjFiltro by mutableStateOf("")
    var tipoFiltro by mutableStateOf("")
    var statusFiltro by mutableStateOf<Boolean?>(true)
    var formEmpresa by mutableStateOf<JuridicoEmpresa?>(null)
        private set
    var formOpen by mutableStateOf(false)
        private set
    var formNome by mutableStateOf("")
    var formCnpj by mutableStateOf("")
    var formTipo by mutableStateOf("")

    fun init() = load()

    fun load() {
        if (loading) return
        loading = true
        scope.launch {
            try {
                val result = repository.list(nomeFiltro, cnpjFiltro, tipoFiltro, statusFiltro, page, PAGE_SIZE)
                empresas = result.content
                totalPages = result.totalPages.coerceAtLeast(1)
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível carregar as empresas."
            } finally {
                loading = false
            }
        }
    }

    fun applyFilters() {
        page = 0
        load()
    }

    fun goToPage(newPage: Int) {
        page = newPage.coerceIn(0, totalPages - 1)
        load()
    }

    fun openCreate() {
        formOpen = true
        formEmpresa = null
        formNome = ""
        formCnpj = ""
        formTipo = ""
    }

    fun openEdit(empresa: JuridicoEmpresa) {
        formOpen = true
        formEmpresa = empresa
        formNome = empresa.nome
        formCnpj = formatCnpj(empresa.cnpj)
        formTipo = empresa.tipo
    }

    fun closeForm() {
        if (!busy) {
            formOpen = false
            formEmpresa = null
        }
    }

    fun save() {
        if (busy || formNome.isBlank() || formCnpj.filter(Char::isDigit).length != 14 || formTipo.isBlank()) return
        busy = true
        scope.launch {
            try {
                val saved = formEmpresa
                if (saved == null) {
                    repository.create(formNome.trim(), formCnpj.filter(Char::isDigit), formTipo.trim())
                } else {
                    repository.update(saved.id, formNome.trim(), formCnpj.filter(Char::isDigit), formTipo.trim())
                }
                formOpen = false
                formEmpresa = null
                load()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível salvar a empresa."
            } finally {
                busy = false
            }
        }
    }

    fun toggleStatus(empresa: JuridicoEmpresa) {
        if (busy) return
        busy = true
        scope.launch {
            try {
                repository.changeStatus(empresa.id, !empresa.ativo)
                load()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível alterar o status."
            } finally {
                busy = false
            }
        }
    }

    fun updateCnpj(value: String) {
        formCnpj = formatCnpj(value)
    }

    val formValid: Boolean
        get() = formNome.isNotBlank() && formTipo.isNotBlank() && formCnpj.filter(Char::isDigit).length == 14

    private fun formatCnpj(value: String): String {
        val digits = value.filter(Char::isDigit).take(14)
        return buildString {
            digits.forEachIndexed { index, digit ->
                if (index == 2 || index == 5) append('.')
                if (index == 8) append('/')
                if (index == 12) append('-')
                append(digit)
            }
        }
    }

    private companion object {
        const val PAGE_SIZE = 20
    }
}

@Composable
fun rememberJuridicoEmpresasController(): JuridicoEmpresasController {
    val scope = rememberCoroutineScope()
    val repository = LocalAppGraph.current.juridicoEmpresaRepository
    return remember { JuridicoEmpresasController(repository, scope) }
}
